import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import LdsButton from "../../components/LdsButton";
import { SideBar } from "../../components/SideBar";
import { useSelector } from "react-redux";

import { ReactLoaderSpinner } from "../../components/ReactLoaderSpinner/index.js";
import LdsImage from "../../components/LdsImage";
import References from "../../components/References";
import NegativeDonut from "../../assets/module-1/1-2-3-negative-donut.svg";
import PositiveDonut from "../../assets/module-1/1-2-3-positive-donut.svg";

// services
import { useBookmarkModuleMutation } from "../../store/services/moduleServerApi";

const Module_1_2_3 = () => {
  const { pathname: pathName } = useLocation();
  const { menuItems } = useSelector((state) => state?.moduleSlice);

  const [bookmarkModule, { isLoading: isMenuLoading }] =
    useBookmarkModuleMutation();

  /* Start useEffect */
  useEffect(() => {
    bookmarkModule({ pathName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // * onPageLoad
  /* End useEffect */

  return (
    <div className="row">
      {isMenuLoading && <ReactLoaderSpinner />}
      {menuItems?.id && <SideBar menuItems={[menuItems]} />}
      <div className="col-sm-8">
        <main>
          <section className="mb-5">
            <h1 className="blue text-uppercase b mt-5">
              1.2.3 Amyvid Is Accurate<sup>1,2</sup>
            </h1>
            <p className="mt-1 mb-0">Estimated time to complete: 1 minute</p>
          </section>
          <section className="row">
            <p>
              Amyvid demonstrated accuracy for estimating beta-amyloid neuritic plaque density.<sup>1</sup>
            </p>
            <h2 className="h5 mb-4 mt-4 b text-center">
              In a clinical trial, Amyvid identified<sup>1</sup>
            </h2>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <h3 className="h5 b orange text-center mb-4">
                  Positive cases (sensitivity)
                </h3>
                <LdsImage
                  src={PositiveDonut}
                  alt="Positive cases (sensitivity) statistic showing 96% of the time, with a 95% confidence interval of 80-100%."
                  style={{ maxWidth: "300px" }}
                  className="mt-3 mb-3 mx-auto d-block"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <h3 className="h5 b blue text-center mb-4">Negative cases (specificity)</h3>
                <LdsImage
                  src={NegativeDonut}
                  alt="Negative cases (specificity) statistic showing 100% of the time, with a 95% confidence interval of 78-100%."
                  style={{ maxWidth: "325px" }}
                  className="mt-3 mb-3 mx-auto d-block"
                />
              </div>
            </div>
            <p className="mt-4">
              Objective evidence of amyloid pathology based on autopsies
              performed within 1 year of Amyvid scan (n=46).<sup>1</sup>
            </p>

            <ul className="mb-4 ps-4">
              <li>
                Amyvid identified positive cases (sensitivity) 96% of the time (95% CI: 80% to 100%)
              </li>
              <li>
                Amyvid identified negative cases (specificity) 100% of the time (95% CI: 78% to 100%)
              </li>
            </ul>
            <p>
              In patients who had an autopsy within 2 years of PET imaging (n=59), sensitivity* was 92% (95% CI: 78% to 98%) and specificity* was 100% (95% CI: 80% to 100%).<sup>1</sup>
            </p>
          </section>
          <section>
            <div className="blue-square-border p-3 light-blue-bg fz-sm p-4">
              <h2 className="p b pb-3 mb-0 black">Results</h2>
              <p className="fz-sm">
                In patients who had an autopsy within 1 year of PET imaging
                (n=46)<sup>1,2</sup>:
              </p>
              <ul className="ms-1 ps-2">
                <li>28 had moderate to frequent plaques and 18 had no or sparse neuritic plaques at autopsy</li>

                <li>
                  The majority of readers rated Amyvid PET scans as positive in
                  27 of the 28 individuals (1 false positive), giving a
                  sensitivity* of 96% (95% CI: 80% to 100%)
                </li>
                <li>
                  The majority of readers rated Amyvid PET scans as negative in
                  all 18 individuals (0 false negatives), which corresponded to
                  a specificity* of 100% (95% CI: 78% to 100%)
                </li>
              </ul>
              <p className="fz-sm">In patients who had an autopsy within 2 years of PET imaging (n=59), sensitivity* was 92% (95% CI: 78% to 98%) and specificity* was 100% (95% CI: 80% to 100%)<sup>1</sup></p>
            </div>
            <div className="blue-square-border p-0">
              <h2 className="p b pt-3 pb-3 blue-bg white text-center mb-0">Study 2: Assessed the sensitivity and specificity of a binary visual read methodology in patients who underwent autopsy (truth standard).<sup>1</sup></h2>
            </div>
            <div className="blue-square-border p-3 light-blue-bg fz-sm">
              <h2 className="p b pb-3 mb-0 black">Design</h2>
              <ul className="ms-1 ps-2">
                <li>5 readers (trained in person) </li>
                <li>Binary visual read methodology</li>
              </ul>
            </div>
            <div className="blue-square-border p-3 light-blue-bg notop">
              <h2 className="p b pt-3 pb-3 mb-0 black">Population</h2>

              <ul className="ms-1 ps-2 fz-sm">
                <li>59 terminally ill patients who received premortem Amyvid scans and then underwent autopsy to establish a truth standard</li>
                <ul className="ms-1 ps-2" style={{ listStyleType: "disc" }}>
                  <li>Of these 59 patients, 29 patients had an Alzheimer’s disease (AD) clinical diagnosis, 13 had another type of dementing disorder, 12 had no history of cognitive impairment, and 5 had MCI</li>
                </ul>
              </ul>
            </div>
          </section>
          <section className="button-control mb-0">
            <LdsButton
              classes="white-button mt-3 mb-5 prev"
              onClick={function navClick() {
                window.open("/module-1/module-1-2-2", "_self");
              }}
            >
              Previous Lesson
            </LdsButton>
            <LdsButton
              classes="blue-button mt-3 mb-5 align-self-right float-end next"
              onClick={function navClick() {
                window.open("/module-1/module-1-2-4", "_self");
              }}
            >
              Next Lesson
            </LdsButton>
          </section>
          <section className="row mt-0 mb-5">
            <p className="fz-sm">
              *Sensitivity and specificity were calculated using the majority
              interpretation of the readers for the presence or absence of
              moderate to frequent amyloid plaques.<sup>2</sup>
            </p>
            <p className="fz-sm">
              CI=confidence interval; MCI=mild-cognitive impairment;
              PET=positron emission tomography.
            </p>
          </section>
          <References>
            <ol className="no-indent fz-sm">
              <li>
                Amyvid (florbetapir F 18 injection) Prescribing Information.
                Lilly USA, LLC.{" "}
              </li>
              <li>
                Clark CM, et al. <em>Lancet Neurol.</em> 2012;11(8):669-678.{" "}
              </li>
            </ol>
          </References>
        </main>
      </div>
    </div>
  );
};

export default Module_1_2_3;
